import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { PLATFORMS, contentTypeLabel, type ContentPlanOutput, type ContentAuditOutput } from "@shared/content.ts";

const STAGE_COLOR: Record<string, string> = { TOFU: "mh-pill--testing", MOFU: "mh-pill--watch", BOFU: "mh-pill--winner" };
const SEV: Record<string, string> = { high: "mh-pill--problem", medium: "mh-pill--watch", low: "mh-pill--neutral" };

export default function Content() {
  const { activeBrand } = useBrands();
  const [tab, setTab] = useState<"plan" | "audit">("plan");

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">คิดคอนเทนต์ <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>Content Studio</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b></> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={"mh-btn mh-btn--sm " + (tab === "plan" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("plan")}>สร้างแผน</button>
          <button className={"mh-btn mh-btn--sm " + (tab === "audit" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("audit")}>ตรวจคอนเทนต์เดิม</button>
        </div>
      </div>
      {tab === "plan" ? <PlanTab brandId={activeBrand?.id ?? null} /> : <AuditTab brandId={activeBrand?.id ?? null} />}
    </div>
  );
}

function PlanTab({ brandId }: { brandId: string | null }) {
  const [days, setDays] = useState(7);
  const [platforms, setPlatforms] = useState<string[]>(["Facebook", "TikTok"]);
  const [out, setOut] = useState<ContentPlanOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => { load(); }, [brandId]);
  async function load() { if (!brandId) return setHistory([]); const r = await api.get<{ plans: any[] }>(`/content?brandId=${brandId}`); setHistory(r.plans); }
  const toggle = (p: string) => setPlatforms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  async function generate() {
    if (!brandId) { setErr("เลือกแบรนด์ก่อน"); return; }
    if (!platforms.length) { setErr("เลือกอย่างน้อย 1 แพลตฟอร์ม"); return; }
    setErr(null); setLoading(true); setOut(null);
    try {
      const r = await api.post<any>("/content/plan", { brandId, inputs: { rangeDays: days, platforms } });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock); load();
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); } finally { setLoading(false); }
  }
  async function open(id: string) { setLoading(true); try { const r = await api.get<{ plan: any }>(`/content/${id}`); setOut(r.plan.data); setIsMock(false); } finally { setLoading(false); } }

  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="form-grid" style={{ marginBottom: 12 }}>
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">ระยะเวลา</label>
            <select className="mh-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 วัน</option><option value={14}>14 วัน</option><option value={30}>30 วัน</option>
            </select>
          </div>
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">แพลตฟอร์ม</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {PLATFORMS.map((p) => (
                <button key={p} className={"mh-btn mh-btn--sm " + (platforms.includes(p) ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => toggle(p)}>{p}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={generate} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="PenLine" size={18} /> สร้างแผนคอนเทนต์</>}</button>
          {history.length > 0 && (
            <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && open(e.target.value)}>
              <option value="">📁 แผนเดิม ({history.length})</option>
              {history.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          )}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังสร้างแผน...</p></div>}

      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>แผนจาก <b>Mock</b> — ใส่ Claude Key เพื่อคอนเทนต์สร้างสรรค์จริง</div></div>}
          <div className="mh-card card-pad" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, marginBottom: 10 }}>Content Pillars</h3>
            <div className="cards">
              {out.pillars.map((p, i) => (
                <div key={i} style={{ background: "var(--gray-050)", borderRadius: 10, padding: 12 }}>
                  <div className="mh-spread"><b>{p.name}</b><span className="mh-pill mh-pill--neutral">{p.ratio}</span></div>
                  <div className="mh-sec" style={{ fontSize: 13, marginTop: 4 }}>{p.description}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {out.items.map((it, i) => (
              <div className="mh-card card-pad" key={i}>
                <div className="mh-spread" style={{ marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                  <div className="mh-row" style={{ gap: 8 }}>
                    <span className="qa__icon" style={{ width: 30, height: 30, fontWeight: 700, fontSize: 13 }}>D{it.day}</span>
                    <b>{contentTypeLabel(it.type)}</b>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <span className="mh-pill mh-pill--neutral">{it.platform}</span>
                    <span className={"mh-pill " + (STAGE_COLOR[it.funnelStage] || "mh-pill--neutral")}>{it.funnelStage}</span>
                  </div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{it.topic}</div>
                <div style={{ marginTop: 6, fontSize: 13.8 }}><span className="mh-muted">Hook:</span> {it.hook}</div>
                <div style={{ marginTop: 4, fontSize: 13.8, whiteSpace: "pre-wrap", background: "var(--gray-050)", borderRadius: 8, padding: "8px 10px" }}>{it.caption}</div>
                <div className="mh-row" style={{ gap: 12, marginTop: 6, flexWrap: "wrap", fontSize: 12.5, color: "var(--text-muted)" }}>
                  <span>🎯 {it.cta}</span><span>🖼️ {it.visualConcept}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function AuditTab({ brandId }: { brandId: string | null }) {
  const [content, setContent] = useState("");
  const [out, setOut] = useState<ContentAuditOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function audit() {
    if (!content.trim()) { setErr("วางคอนเทนต์เดิมก่อน"); return; }
    setErr(null); setLoading(true); setOut(null);
    try {
      const r = await api.post<any>("/content/audit", { brandId, content });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock);
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); } finally { setLoading(false); }
  }

  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <label className="mh-label">วางคอนเทนต์เดิม (แยกแต่ละโพสต์ด้วยการเว้นบรรทัด)</label>
        <textarea className="mh-textarea" style={{ minHeight: 160 }} value={content} onChange={(e) => setContent(e.target.value)} placeholder={"โพสต์ 1...\n\nโพสต์ 2...\n\nโพสต์ 3..."} />
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={audit} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="Radar" size={18} /> ตรวจหา Gap</>}</button>
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังตรวจ...</p></div>}

      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>ตรวจด้วย <b>Mock</b> — ใส่ Claude Key เพื่อวิเคราะห์เชิงลึกจริง</div></div>}
          <div className="form-grid" style={{ alignItems: "start" }}>
            <div className="mh-card card-pad">
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>Content Mix</h3>
              {out.contentMix.map((m, i) => <div key={i} className="mh-spread" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}><span>{m.type}</span><b>{m.count}</b></div>)}
            </div>
            <div className="mh-card card-pad">
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>ช่องว่างที่พบ (Gaps)</h3>
              <div style={{ display: "grid", gap: 8 }}>
                {out.gaps.map((g, i) => (
                  <div key={i}><span className={"mh-pill " + SEV[g.severity]}>{g.severity}</span> <b style={{ fontSize: 13.8 }}>{g.area}</b><div className="mh-sec" style={{ fontSize: 13 }}>{g.note}</div></div>
                ))}
                {out.gaps.length === 0 && <div className="mh-pill mh-pill--winner">ครบถ้วนดี ไม่พบช่องว่างใหญ่</div>}
              </div>
            </div>
          </div>
          <div className="mh-card card-pad" style={{ marginTop: 14 }}>
            <h3 style={{ fontSize: 14, marginBottom: 10 }}>คำแนะนำ (เรียงลำดับ)</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {out.recommendations.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 12 }}>
                  <div className="qa__icon" style={{ width: 28, height: 28, flex: "none", fontWeight: 700 }}>{r.priority}</div>
                  <div><div style={{ fontWeight: 600 }}>{r.action}</div><div className="mh-sec" style={{ fontSize: 13.5 }}>{r.why}</div></div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
